import { Component } from 'react'
import { Form } from '@epages/react-components'
import { bool, func, oneOf, string } from 'prop-types'
import { connect } from 'react-redux'
import { list, map } from 'react-immutable-proptypes'
import { upperFirst } from 'lodash-es'
import Immutable from 'immutable'
import cc from 'classcat'
import slugify from 'speakingurl'

import PageEditPanel, { isStartpage } from './Panels/PageEditPanel'
import translate, { withComponents } from '../../utils/translate'
import { GotoContext } from '../GotoProvider'
import {
  activateEditorSidebarModule,
  createPage,
  deletePage,
  duplicatePage,
  listCategories,
  loadFooterPages,
  loadNavigation,
  updateCategorySettings,
  updateLegalSettings,
  updateNavigation,
  updatePageSettings,
} from '../../store/actions'
import { findDeep, mapDeep } from '../../utils/immutableTreeHelpers'
import { generatePageUrl, generateShopUrl } from '../../urlGenerators'
import Button from '../formComponents/Button'
import ModalDialog from '../ModalDialog'
import compose from '../../utils/compose'
import flatMap from '../../utils/nestedListUtils/flatMap'
import inheritVisibility from '../../utils/inheritVisibility'
import withI18n from '../withI18n'

const keyPrefix = 'components.editorSidebarComponent.pageManagerSection'

export class PageModalRaw extends Component {
  static propTypes = {
    mainNavigation: list.isRequired,
    footerPages: list.isRequired,
    isBeyondShop: bool.isRequired,
    mode: oneOf(['create', 'edit', 'duplicate']).isRequired,
    onRequestClose: func.isRequired,
    t: func.isRequired,
    createPage: func.isRequired,
    duplicatePage: func.isRequired,
    updateNavigation: func.isRequired,
    updatePageSettings: func.isRequired,
    updateCategorySettings: func.isRequired,
    updateLegalSettings: func.isRequired,
    loadNavigation: func.isRequired,
    loadFooterPages: func.isRequired,
    deletePage: func.isRequired,
    getCategoryList: func.isRequired,
    activateEditorSidebarModule: func.isRequired,
    mboBaseUrl: string,
    shopLocale: string,
    shopDefaultLocale: string,
    shopTitle: string,
    page: map,
    isActivePage: bool,
    categories: list,
    lang: string.isRequired,
  }

  static contextType = GotoContext

  constructor(props) {
    super(props)

    this.state = {
      submitting: false,
      submitError: null,
      deleteRequested: false,
    }

    this.slugifyOptions = { lang: props.lang, truncate: 250 }

    const pageType = props.page ? props.page.get('type') : undefined

    this.tModal = (subKey, ...args) => {
      if (props.page) {
        const keyPartForType = {
          category: 'categories',
          blank: 'contentPages',
          legal: 'legalPages',
        }[pageType]

        return props.t(`${keyPrefix}.${keyPartForType}Section.pageSettingsModal.${subKey}`, ...args)
      }

      return props.t(`${keyPrefix}.${'contentPagesSection.createPageModal'}.${subKey}`, ...args)
    }

    this.dialogTitle = this.tModal(`title${pageType === 'blank' ? `.on${upperFirst(this.props.mode)}` : ''}`)

    this.isBeyondHomepage = props.isBeyondShop && Boolean(props.page) && isStartpage(props.page)
  }

  componentDidMount() {
    const { isBeyondShop, categories, getCategoryList } = this.props

    if (isBeyondShop && !categories.size) {
      getCategoryList()
    }
  }

  pageSlugs = flatMap(
    this.props.mainNavigation.concat(this.props.footerPages),
    (entry) => entry.get('type') === 'blank' && entry.get('slug'),
  ).filter(Boolean)

  handlers = {
    onDeleteRequested: () => this.setState({ deleteRequested: true }),
    onDeleteConfirm: (page) => () => this.onSubmitDeletePage(page),
    onCancel: () =>
      this.state.deleteRequested ? this.setState({ deleteRequested: false }) : this.props.onRequestClose(),
    onSubmit: (newPage) => this[`onSubmit${upperFirst(this.props.mode)}Page`](newPage),
  }

  renderDeleteRequest = (pageTitle) => (
    <div className="ep-notification-danger">{this.tModal('deleteButton.warningMessage', { pageTitle })}</div>
  )

  renderError = ({ status, message } = {}) => {
    const maximumPageCountReached = message === 'Maximum page count reached.'

    return (
      <div
        className={cc({
          'ep-form-row': true,
          'ep-alert-info': status >= 500,
          'ep-notification-warning': !(status && status >= 500),
        })}
      >
        {maximumPageCountReached ? (
          <div>
            {withComponents(this.tModal)('errorMessages.maximumPageCountReached', {
              updateLink: (
                <a href={`${this.props.mboBaseUrl}/help`} target="_parent">
                  {this.tModal('errorMessages.updateLink')}
                </a>
              ),
            })}
          </div>
        ) : (
          this.tModal('errorMessages.generic')
        )}
      </div>
    )
  }

  render() {
    const { page, mode, onRequestClose, isBeyondShop, categories, t, mainNavigation } = this.props

    const { submitError, submitting, deleteRequested } = this.state
    const { onSubmit } = this.handlers

    // Only show the delete button for blank pages and not for categories, legal pages or the homepage in Beyond shops
    const showDeleteButton = mode === 'edit' && page.get('type') === 'blank' && !this.isBeyondHomepage

    return (
      <ModalDialog isOpen contentLabel={this.dialogTitle} onRequestClose={onRequestClose}>
        <Form
          name="addPage"
          value={
            mode === 'duplicate'
              ? page.set(
                  'title',
                  `${page.get('title')}-${t(`${keyPrefix}.contentPagesSection.duplicatePageModal.copy`)}`,
                )
              : page
          }
          onSubmit={onSubmit}
          validate={this.validate}
          normalize={this.normalize}
        >
          {({ value: page }) => (
            <>
              <ModalDialog.Header title={this.dialogTitle} />
              <ModalDialog.Body>
                {submitError && this.renderError(submitError)}
                {this.state.deleteRequested ? (
                  this.renderDeleteRequest(page.get('title'))
                ) : (
                  <PageEditPanel
                    changedPage={page}
                    page={this.props.page}
                    tModal={this.tModal}
                    maybeGenerateNewSlug={this.maybeGenerateNewSlug}
                    {...{ isBeyondShop, categories, mode }}
                    mainNavigation={mainNavigation}
                  />
                )}
              </ModalDialog.Body>
              <ModalDialog.Footer>
                {deleteRequested && (
                  <>
                    <Button
                      type="button"
                      variant="cancel"
                      disabled={submitting}
                      onClick={() => this.handlers.onCancel()}
                    >
                      {this.tModal('cancelButton.label')}
                    </Button>

                    <Button
                      type="button"
                      variant="danger"
                      disabled={submitting}
                      onClick={this.handlers.onDeleteConfirm(page)}
                    >
                      {this.tModal('deleteConfirmButton.label')}
                    </Button>
                  </>
                )}

                {!deleteRequested && showDeleteButton && (
                  <Button
                    type="button"
                    variant="danger"
                    disabled={submitting}
                    onClick={() => this.handlers.onDeleteRequested()}
                  >
                    {this.tModal('deleteButton.label')}
                  </Button>
                )}

                {!deleteRequested && (
                  <>
                    <Button
                      type="button"
                      variant="cancel"
                      disabled={submitting}
                      onClick={() => this.handlers.onCancel()}
                    >
                      {this.tModal('cancelButton.label')}
                    </Button>

                    <Button type="submit" disabled={submitting}>
                      {this.tModal('saveButton.label')}
                    </Button>
                  </>
                )}
              </ModalDialog.Footer>
            </>
          )}
        </Form>
      </ModalDialog>
    )
  }

  updateWholeNavigation = (newPage) => {
    // replace changed page in navigation
    const newNavigation = mapDeep(this.props.mainNavigation, (page) =>
      page.get('id') === newPage.get('id') ? newPage : page,
    )

    return this.props.updateNavigation(newNavigation)
  }

  updatePage = (newPage) => {
    const { isActivePage } = this.props
    const title = newPage.get('title')
    const oldPageSlug = newPage.get('slug')
    // did the slug change because of a new title?
    const newPageSlug = this.maybeGenerateNewSlug(oldPageSlug, title)

    const payload = {
      pageId: newPage.get('id'),
      oldPageSlug,
      newPageSlug,
      title,
      titleTag: newPage.get('titleTag'),
      metaDescription: newPage.get('metaDescription'),
      isVisible: newPage.get('isVisible'),
      navigation: newPage.get('navigation'),
    }

    return this.props
      .updatePageSettings(payload)
      .then((updatedPage) => isActivePage && this.context.replace(generatePageUrl(Immutable.fromJS(updatedPage))))
  }

  updateCategory = (newPage) => {
    const payload = {
      categoryId: newPage.get('categoryId'),
      slug: newPage.get('slug'),
      title: newPage.get('title'),
      titleTag: newPage.get('titleTag'),
      metaDescription: newPage.get('metaDescription'),
      isVisible: newPage.get('isVisible'),
      objectId: newPage.get('objectId'),
      facetedSearchCategoryStartDepth: newPage.get('facetedSearchCategoryStartDepth'),
      facetedSearchShowFacetsOnCategory: newPage.get('facetedSearchShowFacetsOnCategory'),
      visibleInNavigation: newPage.get('visibleInNavigation'),
    }

    return this.props.updateCategorySettings(payload)
  }

  updateLegal = (newPage) => {
    const payload = {
      slug: newPage.get('slug'),
      isVisible: newPage.get('isVisible'),
    }

    return this.props.updateLegalSettings(payload)
  }

  validate = (data, isOnSubmit = true) => {
    const normalizedData = this.normalize(data)
    const title = normalizedData.has('title') && normalizedData.get('title')
    const slug = title ? slugify(title, this.slugifyOptions) : ''
    const titleEmptyValidationResult = title ? null : this.tModal('pageNameInputField.validationMessages.isRequired')

    const slugTooShortValidationResult =
      title && slug.length < 1 ? this.tModal('pageNameInputField.validationMessages.onlySpecialCharacters') : null

    const navigationError =
      normalizedData.get('navigation') === 'footer' &&
      this.props.page.get('navigation') === 'main' &&
      this.props.page.get('children') &&
      this.props.page.get('children').size > 0 &&
      this.tModal('pageNameInputField.validationMessages.cannotMakeFooterPageWithChildren')

    return Immutable.fromJS({
      title: isOnSubmit ? titleEmptyValidationResult || slugTooShortValidationResult || navigationError || null : null,
    })
  }

  normalize = (data) => {
    return data.update('title', (title) => title.trim())
  }

  maybeGenerateNewSlug = (oldPageSlug, title) => {
    return oldPageSlug !== slugify(title, this.slugifyOptions) && !this.isBeyondHomepage
      ? this.enforceUniqueSlug(slugify(title, this.slugifyOptions))
      : oldPageSlug
  }

  enforceUniqueSlug = (slug, uniquifier = 1) => {
    if (!this.pageSlugs.includes(slug)) return slug

    return this.pageSlugs.includes(`${slug}-${uniquifier}`)
      ? this.enforceUniqueSlug(slug, uniquifier + 1)
      : `${slug}-${uniquifier}`
  }

  // Auto-checks the "Add pages & content" to-do in the BYD cockpit's dashboard checklist and setup guide
  completeAddPageTask = () => {
    window.parent?.postMessage(
      {
        type: 'CHECKLIST',
        payload: {
          websiteOnly: 'contentSection.addContent',
          regularShop: 'onlineShopSetup.addPages',
        },
      },
      this.props.mboBaseUrl,
    )
  }

  onSubmitCreatePage = async (newPage) => {
    const { goto } = this.context
    const { onRequestClose, createPage, mainNavigation, loadNavigation, loadFooterPages, activateEditorSidebarModule } =
      this.props
    this.setState({ submitting: true })

    const pageParentSlug = newPage.get('pageParentSlug', null)
    const parentPage = findDeep(mainNavigation, (node) => node.get('slug') === pageParentSlug)

    const payload = {
      pageSlug: this.enforceUniqueSlug(slugify(newPage.get('title'), this.slugifyOptions)),
      title: newPage.get('title'),
      pageType: 'blank',
      navigation: newPage.get('navigation'),
      isVisible: parentPage?.get('isVisible') === false ? false : newPage.get('isVisible'),
      collectionId: newPage.getIn(['selectedCollectionInfo', 'isActive'])
        ? newPage.getIn(['selectedCollectionInfo', 'collectionId'])
        : null,
      pageParentSlug,
    }

    try {
      const createdPage = await createPage(payload)
      goto(generatePageUrl(Immutable.fromJS(createdPage)))
      if (!newPage.getIn(['selectedCollectionInfo', 'isActive'])) activateEditorSidebarModule('content-creation')
      if (createdPage.navigation === 'main') await loadNavigation()
      else if (createdPage.navigation === 'footer') await loadFooterPages()
      this.setState({ submitting: false })
      this.completeAddPageTask()
      onRequestClose()
    } catch (err) {
      this.setState({ submitting: false, submitError: err })
    }
  }

  onSubmitDuplicatePage = async (newPage) => {
    const { goto } = this.context
    const { duplicatePage, loadNavigation, loadFooterPages, page, onRequestClose } = this.props

    this.setState({ submitting: true })

    const slug = page.get('slug')
    const payload = {
      newSlug: this.enforceUniqueSlug(slugify(newPage.get('title'), this.slugifyOptions)),
      newTitle: newPage.get('title'),
    }

    try {
      const duplicatedPage = await duplicatePage(slug, payload)
      goto(generatePageUrl(Immutable.fromJS(duplicatedPage)))
      if (duplicatedPage.navigation === 'main') await loadNavigation()
      else if (duplicatedPage.navigation === 'footer') await loadFooterPages()
      this.setState({ submitting: false })
      this.completeAddPageTask()
      onRequestClose()
    } catch (err) {
      this.setState({ submitting: false, submitError: err })
    }
  }

  onSubmitEditPage = (newPage) => {
    const { onRequestClose } = this.props
    const type = newPage.get('type')
    const shouldHideChildren = newPage.get('children').count() > 0 && !newPage.get('isVisible')

    this.setState({ submitting: true })

    const runUpdate = (() => {
      switch (type) {
        case 'blank':
          return shouldHideChildren ? this.updateWholeNavigation(newPage) : this.updatePage(newPage)
        case 'category':
          return this.updateCategory(newPage)
        case 'legal':
          return this.updateLegal(newPage)
        default:
          return Promise.resolve()
      }
    })()

    runUpdate
      .then(() => {
        this.setState({ submitting: false })
        this.completeAddPageTask()
        onRequestClose()
      })
      .catch((submitError) => {
        this.setState({ submitting: false })
        this.setState({ submitError })
      })

    return null
  }

  onSubmitDeletePage = async (page) => {
    const { goto } = this.context
    const { isActivePage, onRequestClose, deletePage } = this.props

    await deletePage(page)

    if (isActivePage) {
      goto(generateShopUrl())
    }

    onRequestClose()

    return null
  }
}

const mapDispatchToProps = (dispatch) => ({
  createPage: (page) => dispatch(createPage(page, { showErrorNotification: false })),
  duplicatePage: (slug, payload) => dispatch(duplicatePage(slug, payload, { showErrorNotification: false })),
  updateNavigation: (newNavigation) =>
    dispatch(
      updateNavigation(inheritVisibility(newNavigation), {
        showErrorNotification: false,
      }),
    ),
  updatePageSettings: (settings) => dispatch(updatePageSettings(settings, { showErrorNotification: false })),
  updateCategorySettings: (settings) => dispatch(updateCategorySettings(settings, { showErrorNotification: false })),
  updateLegalSettings: (settings) => dispatch(updateLegalSettings(settings, { showErrorNotification: false })),
  deletePage: (page) => dispatch(deletePage(page.get('slug'))),
  getCategoryList: () => dispatch(listCategories()),
  loadNavigation: () => dispatch(loadNavigation()),
  loadFooterPages: () => dispatch(loadFooterPages()),
  activateEditorSidebarModule: (name) => dispatch(activateEditorSidebarModule(name)),
})

export default compose(
  withI18n('interface'),
  translate(),
  connect(
    (state) => ({
      isBeyondShop: Boolean(state.getIn(['shop', 'beyond'])),
      mboBaseUrl: state.getIn(['shop', 'mboBaseUrl']),
      categories: state.get('categoriesList').toList(),
      lang: state.getIn(['shop', 'locale'], '').substr(0, 2),
    }),
    mapDispatchToProps,
  ),
)(PageModalRaw)
